import {
  useTranslation
} from "../../../../chunk-6N4NEKWU.mjs";
import "../../../../chunk-D57E6H3M.mjs";

// src/components/composite/Timeline/InlineForm/InlineForm.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdOutlineAssignment } from "react-icons/md";
import { Button, Field, Text, TextField } from "../../../primitive/index.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
var EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)*\.[^\s@.]{2,}$/;
var PHONE_PATTERN = /^\+?[\d\s\-().]{6,}$/;
var MASK_VISIBLE_CHARS = 4;
var EMPTY_VALUE = "\u2014";
function maskValue(value) {
  if (value.length <= MASK_VISIBLE_CHARS) return "\u2022".repeat(value.length);
  return `${"\u2022".repeat(value.length - MASK_VISIBLE_CHARS)}${value.slice(-MASK_VISIBLE_CHARS)}`;
}
function summaryValue(field, value) {
  if (!value) return EMPTY_VALUE;
  return field.masked ? maskValue(value) : value;
}
function InlineForm({
  fields,
  title,
  submitLabel,
  onSubmit,
  onDismiss,
  isDismissed = false,
  dashboardMode = false,
  readOnly = false
}) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState({});
  const [shownErrors, setShownErrors] = useState({});
  const [sendFailed, setSendFailed] = useState(false);
  const [submittedValues, setSubmittedValues] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputElementsRef = useRef({});
  const cardRef = useRef(null);
  const titleRef = useRef(null);
  const focusSummaryRef = useRef(false);
  const [sentHere, setSentHere] = useState(false);
  const prefilledValues = useMemo(() => {
    if (!fields.some((f) => f.value != null)) return null;
    return Object.fromEntries(fields.map((f) => [f.name, f.value ?? ""]));
  }, [fields]);
  const validate = useCallback((field, raw) => {
    const value = raw.trim();
    if (!value) return field.required ? t("inlineForm.errorRequired") : null;
    switch (field.type) {
      case "email":
        return EMAIL_PATTERN.test(value) ? null : t("inlineForm.errorEmail");
      case "tel":
        return PHONE_PATTERN.test(value) ? null : t("inlineForm.errorPhone");
      case "number":
        return Number.isFinite(Number(value)) ? null : t("inlineForm.errorNumber");
      default:
        return null;
    }
  }, [t]);
  const errors = useMemo(() => {
    const next = {};
    for (const field of fields) {
      const error = validate(field, drafts[field.name] ?? "");
      if (error) next[field.name] = error;
    }
    return next;
  }, [fields, drafts, validate]);
  const handleChange = (field, value) => {
    setDrafts((prev) => ({ ...prev, [field.name]: value }));
    if (shownErrors[field.name]) {
      const error = validate(field, value);
      setShownErrors(({ [field.name]: _shown, ...rest }) => error ? { ...rest, [field.name]: error } : rest);
    }
  };
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setShownErrors(errors);
    setSendFailed(false);
    const firstInvalid = fields.find((f) => errors[f.name]);
    if (firstInvalid) {
      inputElementsRef.current[firstInvalid.name]?.focus();
      return;
    }
    const values = Object.fromEntries(
      fields.map((f) => [f.name, (drafts[f.name] ?? "").trim()])
    );
    setIsSubmitting(true);
    try {
      await onSubmit(values);
      focusSummaryRef.current = cardRef.current?.matches(":focus-within") ?? false;
      setSentHere(true);
      setSubmittedValues(values);
    } catch {
      setSendFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDismiss = onDismiss && (() => {
    focusSummaryRef.current = cardRef.current?.matches(":focus-within") ?? false;
    onDismiss();
  });
  useEffect(() => {
    if (!focusSummaryRef.current || !(submittedValues || isDismissed)) return;
    focusSummaryRef.current = false;
    titleRef.current?.focus();
  }, [submittedValues, isDismissed]);
  if (!fields.length) return null;
  const resolvedValues = submittedValues ?? prefilledValues;
  const heading = title ?? t("inlineForm.heading");
  const header = /* @__PURE__ */ jsxs("div", { className: "buyscout-inline-form-header", children: [
    /* @__PURE__ */ jsx("span", { className: "buyscout-timeline-header-icon", "aria-hidden": "true", children: /* @__PURE__ */ jsx(MdOutlineAssignment, {}) }),
    /* @__PURE__ */ jsx(Text, { ref: titleRef, tabIndex: -1, variant: "body-bold", as: "span", className: "buyscout-inline-form-title", children: heading })
  ] });
  const status = /* @__PURE__ */ jsx("div", { className: "buyscout-inline-form-status", role: "status", children: sentHere ? t("inlineForm.sent") : "" });
  if (dashboardMode || isDismissed || readOnly || resolvedValues) {
    let summary;
    if (isDismissed) {
      summary = /* @__PURE__ */ jsx(Text, { variant: "body", as: "div", className: "buyscout-inline-form-summary-empty", children: t("inlineForm.skipped") });
    } else if (resolvedValues) {
      summary = fields.map((field) => {
        const value = resolvedValues[field.name] ?? "";
        return /* @__PURE__ */ jsx(Field, { labelWidth: "sm", align: "start", label: field.label, children: summaryValue(field, value) }, field.name);
      });
    } else {
      summary = /* @__PURE__ */ jsx(Text, { variant: "body", as: "div", className: "buyscout-inline-form-summary-empty", children: t("inlineForm.notSubmitted") });
    }
    return /* @__PURE__ */ jsxs("div", { ref: cardRef, className: "buyscout-inline-form buyscout-inline-form--completed", children: [
      status,
      header,
      /* @__PURE__ */ jsx("div", { className: "buyscout-inline-form-summary", children: summary })
    ] });
  }
  const idleLabel = submitLabel ?? t("inlineForm.submit");
  return /* @__PURE__ */ jsxs("div", { ref: cardRef, className: "buyscout-inline-form", children: [
    status,
    header,
    /* @__PURE__ */ jsxs(
      "form",
      {
        className: "buyscout-inline-form-body",
        noValidate: true,
        "aria-busy": isSubmitting || void 0,
        onSubmit: (e) => {
          e.preventDefault();
          void handleSubmit();
        },
        children: [
          /* @__PURE__ */ jsx("div", { className: "buyscout-inline-form-fields", children: fields.map((field) => {
            const error = shownErrors[field.name];
            return /* @__PURE__ */ jsx(
              TextField,
              {
                ref: (node) => {
                  inputElementsRef.current[field.name] = node;
                },
                label: field.label,
                type: field.type ?? "text",
                size: "sm",
                fullWidth: true,
                className: field.required ? "buyscout-timeline-field buyscout-inline-form-field--required" : "buyscout-timeline-field",
                placeholder: field.placeholder,
                autoComplete: field.autocomplete,
                required: field.required,
                readOnly: isSubmitting,
                value: drafts[field.name] ?? "",
                error: Boolean(error),
                helperText: error,
                onChange: (e) => handleChange(field, e.target.value)
              },
              field.name
            );
          }) }),
          sendFailed && /* @__PURE__ */ jsx(Text, { variant: "body-sm", as: "div", role: "alert", className: "buyscout-inline-form-send-failed", children: t("inlineForm.sendFailed") }),
          /* @__PURE__ */ jsxs("div", { className: "buyscout-inline-form-footer", children: [
            handleDismiss && /* @__PURE__ */ jsx(
              Button,
              {
                variant: "borderless",
                color: "neutral",
                type: "button",
                size: "sm",
                className: "buyscout-inline-form-skip",
                disabled: isSubmitting,
                onClick: handleDismiss,
                children: t("inlineForm.skip")
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "prominent",
                type: "submit",
                size: "sm",
                className: "buyscout-inline-form-submit",
                loading: isSubmitting,
                children: idleLabel
              }
            )
          ] })
        ]
      }
    )
  ] });
}
InlineForm.displayName = "InlineForm";
export {
  InlineForm
};
//# sourceMappingURL=InlineForm.mjs.map